import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useAcknowledgeSignal } from "../../lib/acknowledge.js";
import { cn } from "../../lib/cn.js";
import { ACKNOWLEDGE_SIGNALS } from "../../lib/features.js";
import { formatCrore, istDateTime } from "../../lib/format.js";
import type { SectionId } from "../../pages/symbol/sectionOrder.js";
import { ThemeScope } from "../chart/kit/ThemeScope.js";
import { OutcomeTiles } from "../outcomes/OutcomeTiles.js";
import { SEVERITY_CLASS } from "../signals/severity.js";
import { ClefRating } from "./ClefRating.js";
import { FocusChart } from "./FocusChart.js";
import { SignalFacts } from "./SignalFacts.js";
import { SignalPlan } from "./SignalPlan.js";
import { type SignalDetailData, useSignalDetailData } from "./useSignalDetailData.js";

/**
 * One signal in depth, and the same body behind both shells (decision D5) —
 * the modal the feed opens and the route a shared link lands on.
 *
 * The lead is `explanation.sentence`, which is the whole reason
 * `describeSignal` exists: the reader gets a sentence, not an enum and a
 * key-value dump of the payload.
 */

/** Typed against SectionId, so a link to a section that does not exist is a compile error. */
const SIGNALS_SECTION: SectionId = "signals";

export function SignalDetailBody({
  signalId,
  onOpenSignal,
}: {
  signalId: string;
  onOpenSignal?(id: string): void;
}) {
  const data = useSignalDetailData(signalId);
  // The unfiltered feed key: this body is opened from the modal and from the
  // route, and neither knows which filters the feed behind it is holding. The
  // mutation invalidates every feed key on settle, so the one on screen
  // refreshes whatever it is.
  const acknowledge = useAcknowledgeSignal({});
  return (
    <SignalDetailView
      data={data}
      rating={<ClefRating signalId={signalId} />}
      onAcknowledge={(id) => acknowledge.mutate(id)}
      {...(onOpenSignal ? { onOpenSignal } : {})}
    />
  );
}

/**
 * The presentation half, given the query result rather than running it.
 *
 * That seam is what makes every state testable without a query client and
 * without mocking a module.
 */
export function SignalDetailView({
  data,
  rating,
  onAcknowledge,
  onOpenSignal,
}: {
  data: SignalDetailData;
  /** Clef's shadow rating. A slot, so this half stays testable without a query client. */
  rating?: ReactNode;
  onAcknowledge?(id: string): void;
  onOpenSignal?(id: string): void;
}) {
  if (data.isPending) return <p className="text-text-muted">Loading the signal…</p>;
  // A failed query must not render as an empty body: "nothing here" and "the
  // request failed" are different answers and a reader cannot tell them apart.
  if (data.isError) {
    return <p className="text-danger">{data.error?.message ?? "Could not load the signal."}</p>;
  }
  if (!data.signal || !data.context) {
    return <p className="text-text-muted">No signal to show.</p>;
  }

  const { signal, context } = data;
  const acknowledged = signal.acknowledgedAt !== null;

  return (
    <article className="flex flex-col gap-4">
      <header className="flex flex-wrap items-baseline gap-3">
        <span
          className={cn(
            "border px-2 py-0.5 text-xs",
            SEVERITY_CLASS[signal.severity] ?? SEVERITY_CLASS.info,
          )}
        >
          {signal.severity}
        </span>
        <h2 className="text-xl font-semibold">{signal.symbol}</h2>
        <span className="text-sm text-text-muted">{context.name}</span>
        {/* The size the Size filter bands on. The route already reads it for
            this row; without it here the modal fetched a value, serialised it,
            and threw it away, and a reader who filtered to Mid had no way to
            check why this name answered. */}
        {signal.marketCap !== null ? (
          <span className="text-sm text-text-muted tabular-nums" data-testid="signal-market-cap">
            ₹{formatCrore(signal.marketCap)} Cr
          </span>
        ) : null}
        <time className="ml-auto text-sm text-text-muted" dateTime={signal.occurredAt}>
          {istDateTime(signal.occurredAt)}
        </time>
      </header>

      <p className="text-lg text-text">{signal.explanation.sentence}</p>

      <SignalPlan signal={signal} />

      {rating}

      <SignalFacts explanation={signal.explanation} />

      <section className="flex flex-col gap-2">
        <h3 className="text-sm text-text-muted">
          What this signal type has been worth, across every name
        </h3>
        {context.outcomes ? (
          <OutcomeTiles panel={context.outcomes} />
        ) : (
          // Not empty tiles: a type nothing has answered yet is a different
          // fact from a type that returned nothing.
          <p className="text-sm text-text-muted">
            No outcome for this signal type yet. The first answer lands five sessions after a signal
            fires.
          </p>
        )}
      </section>

      <ThemeScope>
        <FocusChart
          bars={context.bars}
          nearby={context.nearby}
          highlightLabel={signal.explanation.title}
          highlightSessionDate={signal.sessionDate}
        />
      </ThemeScope>

      {onOpenSignal && context.nearby.length > 0 ? (
        <section className="flex flex-col gap-2" data-testid="nearby-signals">
          <h3 className="text-sm text-text-muted">Also on this name, in this window</h3>
          <ul className="flex flex-col gap-1">
            {context.nearby.map((other) => (
              <li key={other.id}>
                <button
                  type="button"
                  onClick={() => onOpenSignal(other.id)}
                  className="text-left text-sm text-accent hover:underline"
                >
                  {other.sessionDate} · {other.explanation.title}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <footer className="flex flex-wrap items-center gap-4 text-sm">
        <Link
          to="/symbol/$symbol"
          params={{ symbol: signal.symbol }}
          search={{ section: SIGNALS_SECTION }}
          className="text-accent hover:underline"
        >
          Open {signal.symbol}
        </Link>
        {/* Only for a signal with a plan: the form opens filled with it. */}
        {signal.trade ? (
          <Link
            to="/trades"
            search={{
              symbol: signal.symbol,
              side: signal.trade.plan.side,
              entry: signal.trade.plan.entry,
              ...(signal.trade.plan.stop === null ? {} : { stop: signal.trade.plan.stop }),
              ...(signal.trade.plan.target === null ? {} : { target: signal.trade.plan.target }),
              signalId: signal.id,
            }}
            className="text-accent hover:underline"
          >
            Track this trade
          </Link>
        ) : null}
        {/* Both halves behind the flag: with the feature off nothing can set
            `acknowledgedAt`, so the stamp would only ever surface rows an old
            build was clicked on, beside a button that no longer belongs. */}
        {ACKNOWLEDGE_SIGNALS && acknowledged ? (
          <span className="text-text-muted" data-testid="acknowledged">
            Acknowledged {istDateTime(signal.acknowledgedAt)}
          </span>
        ) : null}
        {ACKNOWLEDGE_SIGNALS && !acknowledged ? (
          <button
            type="button"
            onClick={() => onAcknowledge?.(signal.id)}
            className="border border-border bg-surface px-3 py-1 text-text-muted"
          >
            Mark as read
          </button>
        ) : null}
      </footer>
    </article>
  );
}
